import React, { useState } from 'react';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { arrayOf, array, string, bool, func, number } from 'prop-types';
import { THUMB_TYPE_DISLIKE, THUMB_TYPE_LIKE, propTypes } from '../../util/types';
import classNames from 'classnames';
import {
  Avatar,
  UserDisplayName,
  ExpandableBio,
  InlineTextButton,
  Badges,
  ActionButton,
  Icon,
} from '../../components';
import { CommentForm } from '../../forms';
import moment from 'moment';

import ControlMenu from './ControlMenu/ControlMenu';
import css from './Comments.module.css';

const COMMENT_LENGTH = 300;

export const Comment = props => {
  const [isEditComment, setEditComment] = useState(false);
  const [isReplying, setIsReplying] = useState(false);

  const {
    comment,
    currentUser,
    currentUserReactions,
    likeOrDislikeInProgress,
    likeOrDislikeError,
    editCommentInProgress,
    editCommentError,
    deleteCommentInProgress,
    deleteCommentError,
    currentDeleteCommentId,
    deletedCommentIds,
    handleCommentLikeOrDislike,
    handleEditComment,
    handleDeleteComment,
    handlePinComment,
    handleSendReply,
    sendReplyInProgress,
    sendReplyError,
    allComments,
    depth = 0,
    canPinComment,
    intl,
  } = props;

  const authorForAvatar = comment.author ? comment.author : { attributes: { deleted: true } };
  const date = comment.attributes.createdAt;

  // Check if comment is soft-deleted (backend marked as deleted)
  const isSoftDeleted = comment.attributes.isDeleted || false;
  const deletedAt = comment.attributes.deletedAt;

  // Check if comment is hard-deleted (frontend tracking)
  const isCurrentlyDeleting = currentDeleteCommentId === comment.id;
  const isHardDeleted = deletedCommentIds?.includes(comment.id) || false;
  const isDeleted = isHardDeleted || isSoftDeleted;

  const isOwnComment = currentUser?.id?.uuid === comment?.author?.id?.uuid;
  // Only show control menu if user can perform at least one action
  const showControlMenu = !isEditComment && !isDeleted && (isOwnComment || canPinComment);
  const thumbButtonDisabled = likeOrDislikeInProgress || !!likeOrDislikeError;

  const isLiked = !!currentUserReactions.find(
    reaction => reaction.commentId === comment.id && reaction.type === 'like'
  );
  const isDisliked = !!currentUserReactions.find(
    reaction => reaction.commentId === comment.id && reaction.type === 'dislike'
  );

  // Calculate net vote count and current user vote for community-style voting
  const voteCount = (comment.attributes.likes || 0) - (comment.attributes.dislikes || 0);
  const userVote = isLiked ? 'like' : isDisliked ? 'dislike' : null;

  const isPinned = comment.attributes?.isPinned ?? false;

  // Handle deleted comments - keep original user info, just show deleted message
  if (isDeleted) {
    // Find child comments (replies) for this comment
    const replies = allComments.filter(c => c.attributes.parentId === comment.id);

    return (
      <div className={css.commentWrapper}>
        <div className={classNames(css.comment, css.deletedComment)}>
          <Avatar
            className={depth > 0 ? css.replyAvatar : css.avatar}
            user={authorForAvatar}
            useUserTier
          />
          <div className={css.commentContent}>
            <div className={css.commentInfo}>
              <div className={css.commentInfoDetails}>
                {comment.author ? (
                  <UserDisplayName
                    className={css.userDisplayName}
                    user={comment.author}
                    intl={intl}
                  />
                ) : (
                  <span className={css.userDisplayName}>
                    {intl.formatMessage({
                      id: 'Comments.deletedUser',
                    })}
                  </span>
                )}
                <Badges user={authorForAvatar} />
                <span className={css.commentDate}>{moment(date).fromNow()}</span>
                {isPinned && (
                  <>
                    <span className={css.pinIcon}>
                      <Icon name="pin" size={16} />
                    </span>
                    <span className={css.pinnedText}>
                      <FormattedMessage id="Comments.pinned" />
                    </span>
                  </>
                )}
              </div>
            </div>
            {/* Replace comment content with deleted message */}
            <div className={css.deletedMessage}>
              <FormattedMessage id="Comments.deletedComment" />
            </div>
            {/* No action buttons for deleted comments */}
          </div>
        </div>

        {/* Render replies to deleted comments if there are any */}
        {replies.length > 0 && (
          <div className={css.replies}>
            {replies.map(reply => (
              <Comment
                key={reply.id}
                comment={reply}
                currentUser={currentUser}
                currentUserReactions={currentUserReactions}
                likeOrDislikeInProgress={likeOrDislikeInProgress}
                likeOrDislikeError={likeOrDislikeError}
                editCommentInProgress={editCommentInProgress}
                editCommentError={editCommentError}
                deleteCommentInProgress={deleteCommentInProgress}
                deleteCommentError={deleteCommentError}
                currentDeleteCommentId={currentDeleteCommentId}
                deletedCommentIds={deletedCommentIds}
                handleCommentLikeOrDislike={handleCommentLikeOrDislike}
                handleEditComment={handleEditComment}
                handleDeleteComment={handleDeleteComment}
                handlePinComment={handlePinComment}
                handleSendReply={handleSendReply}
                sendReplyInProgress={sendReplyInProgress}
                sendReplyError={sendReplyError}
                allComments={allComments}
                depth={depth + 1}
                canPinComment={canPinComment}
                intl={intl}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={css.commentWrapper}>
      <div className={css.comment}>
        {isEditComment ? (
          <CommentForm
            id={`comment_${comment.id}`}
            // component props
            currentUser={currentUser}
            avatarClassName={depth > 0 ? css.replyAvatar : css.avatar}
            sendCommentInProgress={editCommentInProgress}
            sendCommentError={editCommentError}
            submitMessageTranslationId={'Comments.commentEdit'}
            isFocused
            // form props
            initialValues={{
              comment: comment.attributes.content,
            }}
            onSubmit={(values, form) => {
              handleEditComment(comment.id, values.comment).then(() => {
                setEditComment(false);
                form.restart();
              });
            }}
            onCancel={(values, form) => {
              setEditComment(false);
              form.restart();
            }}
          />
        ) : (
          <>
            <Avatar
              className={depth > 0 ? css.replyAvatar : css.avatar}
              user={authorForAvatar}
              useUserTier
            />
            <div className={css.commentContent}>
              <div className={css.commentInfo}>
                <div className={css.commentInfoDetails}>
                  {comment.author ? (
                    <UserDisplayName
                      className={css.userDisplayName}
                      user={comment.author}
                      intl={intl}
                    />
                  ) : (
                    <span className={css.userDisplayName}>
                      {intl.formatMessage({
                        id: 'Comments.deletedUser',
                      })}
                    </span>
                  )}
                  <Badges user={authorForAvatar} />
                  <span className={css.commentDate}>{moment(date).fromNow()}</span>
                  {isPinned && (
                    <>
                      <span className={css.pinIcon}>
                        <Icon name="pin" size={16} />
                      </span>
                      <span className={css.pinnedText}>
                        <FormattedMessage id="Comments.pinned" />
                      </span>
                    </>
                  )}
                </div>
              </div>
              <ExpandableBio
                className={css.commentBio}
                showMoreClassName={css.commentShowMore}
                bio={comment.attributes.content}
                maxLength={COMMENT_LENGTH}
              />
              <div className={css.commentActions}>
                <ActionButton className={css.voteActionButtonWrapper} isButton={false}>
                  <InlineTextButton
                    className={classNames(css.voteActionButton, {
                      [css.voteActionButtonActive]: userVote === 'like',
                    })}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCommentLikeOrDislike('like', comment.id);
                    }}
                    disabled={thumbButtonDisabled}
                    aria-label={intl.formatMessage({ id: 'Comments.upvote' })}
                  >
                    <Icon
                      name="voteUp"
                      size={16}
                      className={css.voteActionIcon}
                      filled={userVote === 'like'}
                    />
                  </InlineTextButton>
                  <span className={css.voteCount}>{voteCount}</span>
                  <InlineTextButton
                    className={classNames(css.voteActionButton, css.dislikeVoteActionButton, {
                      [css.voteActionButtonActive]: userVote === 'dislike',
                    })}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCommentLikeOrDislike('dislike', comment.id);
                    }}
                    disabled={thumbButtonDisabled}
                    aria-label={intl.formatMessage({ id: 'Comments.downvote' })}
                  >
                    <Icon
                      name="voteDown"
                      size={16}
                      className={css.voteActionIcon}
                      filled={userVote === 'dislike'}
                    />
                  </InlineTextButton>
                </ActionButton>
                {/* Reply button */}
                {currentUser?.id && depth < 5 && (
                  <ActionButton
                    label={intl.formatMessage({ id: 'Comments.reply' })}
                    onClick={() => setIsReplying(!isReplying)}
                    disabled={isReplying}
                  >
                    <Icon name="comment" size={16} className={css.actionIcon} />
                  </ActionButton>
                )}
              </div>
            </div>
          </>
        )}
        {showControlMenu ? (
          <ControlMenu
            className={css.controlMenu}
            isPinned={isPinned}
            isOwn={isOwnComment}
            isReply={depth > 0}
            canPinComment={canPinComment}
            deleteCommentInProgress={deleteCommentInProgress}
            deleteCommentError={deleteCommentError}
            onEditComment={() => setEditComment(true)}
            onDeleteComment={() => handleDeleteComment(comment.id)}
            onPinComment={() => handlePinComment(comment.id)}
          />
        ) : null}
      </div>

      {/* Reply form - outside the flex comment container */}
      {isReplying && currentUser?.id && (
        <div className={css.replyForm}>
          <CommentForm
            id={`reply_${comment.id}`}
            currentUser={currentUser}
            avatarClassName={css.replyAvatar}
            sendCommentInProgress={sendReplyInProgress}
            sendCommentError={sendReplyError}
            submitMessageTranslationId="Comments.sendReply"
            isFocused
            initialValues={{
              comment: '',
            }}
            onSubmit={(values, form) => {
              const replyText = values.comment;
              if (handleSendReply) {
                handleSendReply(comment.id, replyText).then(() => {
                  setIsReplying(false);
                  form.restart();
                });
              }
            }}
            onCancel={() => {
              setIsReplying(false);
            }}
          />
        </div>
      )}

      {/* Render replies - outside the flex comment container */}
      {allComments && depth < 5 && renderReplies()}
    </div>
  );

  // Helper function to render replies
  function renderReplies() {
    const replies = allComments.filter(c => c.attributes.parentId === comment.id);
    if (replies.length === 0) return null;

    return (
      <div className={css.replies}>
        {replies.map(reply => (
          <Comment
            key={`Reply_${reply.id}`}
            comment={reply}
            currentUser={currentUser}
            currentUserReactions={currentUserReactions}
            likeOrDislikeInProgress={likeOrDislikeInProgress}
            likeOrDislikeError={likeOrDislikeError}
            editCommentInProgress={editCommentInProgress}
            editCommentError={editCommentError}
            deleteCommentInProgress={deleteCommentInProgress}
            deleteCommentError={deleteCommentError}
            currentDeleteCommentId={currentDeleteCommentId}
            deletedCommentIds={deletedCommentIds}
            handleCommentLikeOrDislike={handleCommentLikeOrDislike}
            handleEditComment={handleEditComment}
            handleDeleteComment={handleDeleteComment}
            handlePinComment={handlePinComment}
            handleSendReply={handleSendReply}
            sendReplyInProgress={sendReplyInProgress}
            sendReplyError={sendReplyError}
            allComments={allComments}
            depth={depth + 1}
            canPinComment={canPinComment}
            intl={intl}
          />
        ))}
      </div>
    );
  }
};

Comment.propTypes = {
  comment: propTypes.comment.isRequired,
  currentUser: propTypes.currentUser,
  currentUserReactions: array,
  likeOrDislikeInProgress: bool,
  likeOrDislikeError: propTypes.error,
  editCommentInProgress: bool,
  editCommentError: propTypes.error,
  deleteCommentInProgress: bool,
  deleteCommentError: propTypes.error,
  handleCommentLikeOrDislike: func.isRequired,
  handleEditComment: func.isRequired,
  handleDeleteComment: func.isRequired,
  handlePinComment: func.isRequired,
  handleSendReply: func,
  sendReplyInProgress: bool,
  sendReplyError: propTypes.error,
  allComments: array,
  depth: number,
  canPinComment: bool,
  intl: intlShape.isRequired,
};

const CommentsComponent = props => {
  const {
    className,
    rootClassName,
    commentsClassName,
    intl,
    comments,
    currentUser,
    currentUserReactions,
    likeOrDislikeInProgress,
    likeOrDislikeError,
    editCommentInProgress,
    editCommentError,
    deleteCommentInProgress,
    deleteCommentError,
    currentDeleteCommentId,
    deletedCommentIds,
    sendReplyInProgress,
    sendReplyError,
    handleCommentLikeOrDislike,
    handleEditComment,
    handleDeleteComment,
    handlePinComment,
    handleSendReply,
    canPinComment,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  // Filter top-level comments (no parentId)
  const topLevelComments = comments.filter(c => !c.attributes.parentId);

  return (
    <div className={classes}>
      <ul className={classNames(css.comments, commentsClassName)}>
        {topLevelComments.map(c => {
          return (
            <li key={`Comment_${c.id}`} className={css.commentItem}>
              <Comment
                comment={c}
                intl={intl}
                currentUser={currentUser}
                currentUserReactions={currentUserReactions}
                likeOrDislikeInProgress={likeOrDislikeInProgress}
                likeOrDislikeError={likeOrDislikeError}
                editCommentInProgress={editCommentInProgress}
                editCommentError={editCommentError}
                deleteCommentInProgress={deleteCommentInProgress}
                deleteCommentError={deleteCommentError}
                currentDeleteCommentId={currentDeleteCommentId}
                deletedCommentIds={deletedCommentIds}
                sendReplyInProgress={sendReplyInProgress}
                sendReplyError={sendReplyError}
                handleCommentLikeOrDislike={handleCommentLikeOrDislike}
                handleEditComment={handleEditComment}
                handleDeleteComment={handleDeleteComment}
                handlePinComment={handlePinComment}
                handleSendReply={handleSendReply}
                allComments={comments}
                depth={0}
                canPinComment={canPinComment}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

CommentsComponent.defaultProps = {
  className: null,
  rootClassName: null,
  commentsClassName: null,
  comments: [],
  currentUserReactions: [],
  likeOrDislikeInProgress: false,
  likeOrDislikeError: null,
  editCommentInProgress: false,
  editCommentError: null,
  deleteCommentInProgress: false,
  deleteCommentError: null,
  currentDeleteCommentId: null,
  deletedCommentIds: [],
  sendReplyInProgress: false,
  sendReplyError: null,
  handleSendReply: null,
  canPinComment: false,
};

CommentsComponent.propTypes = {
  className: string,
  rootCalssName: string,
  commentsClassName: string,
  comments: arrayOf(propTypes.comment),
  currentUserReactions: array,
  likeOrDislikeInProgress: bool.isRequired,
  likeOrDislikeError: propTypes.error,
  editCommentInProgress: bool.isRequired,
  editCommentError: propTypes.error,
  deleteCommentInProgress: bool.isRequired,
  deleteCommentError: propTypes.error,
  currentDeleteCommentId: string,
  deletedCommentIds: arrayOf(string),
  handleCommentLikeOrDislike: func.isRequired,
  handleEditComment: func.isRequired,
  handleDeleteComment: func.isRequired,
  handlePinComment: func.isRequired,
  handleSendReply: func,
  sendReplyInProgress: bool,
  sendReplyError: propTypes.error,
  canPinComment: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

const Comments = injectIntl(CommentsComponent);

export default Comments;
